import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import Hero from "@/components/ui/Hero";
import CTABlock from "@/components/ui/CTABlock";
import Reveal from "@/components/ui/Reveal";
import { BLOG_POSTS } from "@/data/blogPosts";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const fmtDate = (iso) =>
    new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function Blog() {
    useDocumentTitle(
        "Screen Care Guides & Blog · Pool Cage Repair Naples, FL · A1 Screening",
        "Practical guides for Naples and Southwest Florida homeowners: pool cage screen repair vs rescreen, screen lifespan, cost factors, hurricane prep, and mesh options."
    );
    return (
        <div data-testid="blog-page">
            <Hero
                eyebrow="Screen Care Guides"
                title="Pool Cage & Screen Guides for Naples Homeowners"
                subtitle="Straight answers from a family-owned Naples screen company — what wears screens out here, how repairs and rescreens differ, and how to plan ahead."
            />
            <div className="a1-container a1-section">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {BLOG_POSTS.map((post, i) => (
                        <Reveal key={post.slug} delay={i * 60}>
                            <BlogCard post={post} eager={i < 3} />
                        </Reveal>
                    ))}
                </div>
            </div>
            <CTABlock
                headline="Have a screen question of your own?"
                body="Call us or request a free estimate — a technician will look at your enclosure and give you a straight answer."
            />
        </div>
    );
}

function BlogCard({ post, eager }) {
    const base = `/images/gallery/web/${post.heroPhoto}`;
    return (
        <Link
            to={`/blog/${post.slug}`}
            className="a1-card a1-card-link no-underline overflow-hidden p-0 flex flex-col h-full"
            data-testid={`blog-card-${post.slug}`}
        >
            <div className="relative aspect-[16/9] overflow-hidden" style={{ background: "var(--color-bg-alt)" }}>
                <picture>
                    <source type="image/webp" srcSet={`${base}-md.webp`} />
                    <img
                        src={`${base}-md.jpg`}
                        alt=""
                        loading={eager ? "eager" : "lazy"}
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                </picture>
                <span
                    className="absolute top-3 left-3 a1-badge uppercase tracking-wider"
                    style={{ background: "var(--color-accent)", color: "#081123" }}
                >
                    {post.category}
                </span>
            </div>
            <div className="p-5 flex flex-col gap-2 flex-1">
                <h2 className="text-lg font-bold leading-snug m-0" style={{ color: "var(--color-text)" }}>
                    {post.title}
                </h2>
                <p className="text-sm m-0 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                    {post.excerpt}
                </p>
                <p className="text-xs m-0 mt-auto pt-2 flex items-center gap-2" style={{ color: "var(--color-text-dim)" }}>
                    {fmtDate(post.date)} · <Clock size={12} /> {post.readTime} min read
                </p>
            </div>
        </Link>
    );
}
